import { closestCenter, DndContext, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import type { Table } from "@tanstack/react-table";
import { GripVertical, Settings2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { cn } from "@/lib/utils";

interface DataTableViewOptionsProps<TData> {
  table: Table<TData>;
}

interface SortableColumnItemProps {
  id: string;
  children: React.ReactNode;
}

function SortableColumnItem({ id, children }: SortableColumnItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className={cn("flex items-center gap-2", { "opacity-50": isDragging })}>
      <Button variant="ghost" size="icon" className="h-4 w-4 cursor-grab pl-2.5" {...attributes} {...listeners}>
        <GripVertical />
      </Button>
      {children}
    </div>
  );
}

export function DataTableViewOptions<TData>({ table }: DataTableViewOptionsProps<TData>) {
  const columns = table
    .getAllColumns()
    .filter((column) => typeof column.accessorFn !== "undefined" && !column.columnDef.meta?.hiddenColumn);

  const stickToLeftColumns = table.getAllColumns().filter((column) => column.columnDef.meta?.stickToLeft);
  const columnOrder = table.getState().columnOrder;

  // Get columns in saved order, plus any new columns not in the saved order
  const orderedColumns = columnOrder.length
    ? [
        ...columnOrder
          .map((id) => columns.find((col) => col.id === id))
          .filter((col): col is NonNullable<typeof col> => Boolean(col)),
        ...columns.filter((col) => !columnOrder.includes(col.id)),
      ]
    : columns;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const activeIndex = orderedColumns.findIndex((col) => col.id === active.id);
    const overIndex = orderedColumns.findIndex((col) => col.id === over.id);

    if (activeIndex !== -1 && overIndex !== -1) {
      const newOrder = arrayMove(
        orderedColumns.map((col) => col.id),
        activeIndex,
        overIndex,
      );
      newOrder.unshift(...stickToLeftColumns.map((col) => col.id));
      table.setColumnOrder(newOrder);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <DropdownMenu>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="size-9">
                <Settings2 />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <DropdownMenuContent align="end" className="w-[250px]">
            <DropdownMenuLabel className="font-semibold">Manage columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={orderedColumns.map((col) => col.id)} strategy={verticalListSortingStrategy}>
                {orderedColumns.map((column) => {
                  return (
                    <SortableColumnItem key={column.id} id={column.id}>
                      {column.getCanHide() ? (
                        <DropdownMenuCheckboxItem
                          className="flex-1 capitalize"
                          checked={column.getIsVisible()}
                          onSelect={(e) => {
                            e.preventDefault();
                          }}
                          onCheckedChange={(value) => {
                            column.toggleVisibility(!!value);
                          }}
                        >
                          {column.columnDef.meta?.displayName ?? column.id.replace(/_/g, " ")}
                        </DropdownMenuCheckboxItem>
                      ) : (
                        <DropdownMenuItem className="flex-1 capitalize">
                          {column.columnDef.meta?.displayName ?? column.id.replace(/_/g, " ")}
                        </DropdownMenuItem>
                      )}
                    </SortableColumnItem>
                  );
                })}
              </SortableContext>
            </DndContext>
          </DropdownMenuContent>
          <TooltipContent>
            <p>Manage columns</p>
          </TooltipContent>
        </DropdownMenu>
      </Tooltip>
    </TooltipProvider>
  );
}
